import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import KeyIcon from '../components/icons/KeyIcon';
import StayExtensionGuestSection from '../components/StayExtensionGuestSection';
import DepositGuestSection from '../components/DepositGuestSection';
import { LanguageOverride } from '../i18n/LanguageContext';
import { depositText } from '../i18n/pages/deposit';
import {
  getPublicCheckinGuide,
  getManagerGuideOverlay,
  regenerateTtlockPin,
  updateCleaningStatus,
  updateVisibleFrom,
  updateKeyCodeOverride,
  setTtlockPinManually,
  getOfflineTtlockPin,
  getPublicCityTaxSummary,
  createCityTaxCheckoutSession,
  PublicCheckinGuide,
  ManagerGuideOverlay,
  PublicCityTaxSummary,
} from '../api.checkinGuide';

const GUIDE_LANGUAGE_STORAGE_KEY = 'guest-form-language';
type GuideLanguage = 'cs' | 'en';

interface GuideText {
  headerLabel: string;
  managerViewBadge: string;
  inLabel: string;
  outLabel: string;
  statusNotReady: (checkInDate: string, checkInTime: string | null) => string;
  statusReady: string;
  statusExpired: (checkOutDate: string) => string;
  weatherSectionTitle: string;
  loadingGuide: string;
  expiredTitle: string;
  expiredBody: string;
  expiredFooter: string;
  blockedTitle: string;
  blockedBody: string;
  notReadyTitle: string;
  notReadyBody: string;
  finishRegistration: string;
  notFoundTitle: string;
  notFoundBody: string;
  taxStepRegistration: string;
  taxStepPayment: string;
  taxStepGuide: string;
  taxRequiredTitle: string;
  taxDueBannerTitle: string;
  taxDueBannerBody: string;
  taxPayMissingButton: string;
  taxAlreadyPaidLabel: string;
  taxMissingLabel: string;
  taxRequiredBody: string;
  taxConfirming: string;
  taxSummaryError: string;
  taxNights: string;
  taxAdults: string;
  taxChildren: string;
  taxDisabled: string;
  taxTotal: string;
  taxPayButton: string;
  taxPaying: string;
  taxStripeNotice: string;
  untranslatedNote: string;
  payAttentionTitle: string;
  addressTitle: string;
  openInMaps: string;
  keyAccessTitle: string;
  keyAccessLabels: Record<string, string>;
  codeLabel: string;
  codeHiddenUntil: (time: string, date: string) => string;
  pinLabel: string;
  pinHiddenUntil: (time: string, date: string) => string;
  pinValidNote: string;
  lockOfflineNote: string;
  wifiTitle: string;
  networkLabel: string;
  passwordLabel: string;
  parkingTitle: string;
  priceLabel: string;
  contactTitle: string;
  nameLabel: string;
  phoneLabel: string;
  emailLabel: string;
  guideTitle: string;
  checkoutTitle: string;
  photosTitle: string;
  linksTitle: string;
  copyLabel: string;
  copiedLabel: string;
  showMoreLabel: string;
  showLessLabel: string;
  czech: string;
  english: string;
}

const GUIDE_TEXT: Record<GuideLanguage, GuideText> = {
  cs: {
    headerLabel: 'Průvodce ubytováním',
    managerViewBadge: 'POHLED SPRÁVCE',
    inLabel: 'Příjezd',
    outLabel: 'Odjezd',
    statusNotReady: (checkInDate, checkInTime) => `Ještě není připraveno · Check-in od ${checkInDate}${checkInTime ? ' v ' + checkInTime : ''}`,
    statusReady: 'Připraveno k check-inu',
    statusExpired: (checkOutDate) => `Pobyt ukončen · Check-out ${checkOutDate}`,
    weatherSectionTitle: 'Počasí během vašeho pobytu',
    loadingGuide: 'Načítání průvodce…',
    expiredTitle: 'Pobyt ukončen',
    expiredBody: 'Tento průvodce ubytováním již není dostupný — váš pobyt skončil.',
    expiredFooter: 'Doufáme, že se vám pobyt líbil. Těšíme se na příště!',
    blockedTitle: 'Přístup k průvodci zablokován',
    blockedBody: 'Váš hostitel dočasně zablokoval přístup k tomuto průvodci. Kontaktujte ho prosím přímo pro více informací.',
    notReadyTitle: 'Už to nebude dlouho trvat',
    notReadyBody: 'Tento průvodce se odemkne automaticky po dokončení registrace hostů.',
    finishRegistration: 'Dokončit registraci',
    notFoundTitle: 'Průvodce není dostupný',
    notFoundBody: 'Tento odkaz na průvodce ubytováním je neplatný nebo ještě nebyl nastaven.',
    taxStepRegistration: 'Registrace',
    taxStepPayment: 'Platba',
    taxStepGuide: 'Průvodce',
    taxRequiredTitle: 'Místní poplatek',
    taxRequiredBody: 'Před zobrazením průvodce ubytováním je potřeba uhradit místní poplatek.',
    taxDueBannerTitle: 'Místní poplatek zatím není uhrazen',
    taxDueBannerBody: 'Hostitel vám zpřístupnil průvodce dříve, místní poplatek za pobyt je ale stále potřeba uhradit.',
    taxPayMissingButton: 'Doplatit chybějící částku →',
    taxAlreadyPaidLabel: 'Již zaplaceno',
    taxMissingLabel: 'Chybí doplatit',
    taxConfirming: 'Potvrzujeme platbu…',
    taxSummaryError: 'Nepodařilo se načíst částku k úhradě. Zkuste stránku obnovit.',
    taxNights: 'nocí',
    taxAdults: 'dospělí',
    taxChildren: 'děti',
    taxDisabled: 'ZTP/P',
    taxTotal: 'Celkem',
    taxPayButton: 'Zaplatit kartou →',
    taxPaying: 'Přesměrování na platbu…',
    taxStripeNotice: 'Platbu zpracovává Stripe. Vaše platební údaje neukládáme. Stránka se po platbě automaticky odemkne — pokud jste již zaplatili, zkuste stránku obnovit.',
    untranslatedNote: 'Některé části zobrazuje hostitel pouze v původním jazyce — anglický překlad ještě není k dispozici.',
    payAttentionTitle: 'Pozor',
    addressTitle: 'Adresa',
    openInMaps: 'Otevřít v mapách',
    keyAccessTitle: 'Přístup ke klíčům',
    keyAccessLabels: {
      DOOR_CODE: 'Kód na dveřích',
      KEYBOX:    'Klíčovka',
      PERSON:    'Osobně / u hostitele',
      NONE:      'Klíč není potřeba',
      TTLOCK:    'Chytrý zámek',
    },
    codeLabel: 'Kód',
    codeHiddenUntil: (time, date) => `Kód je momentálně skrytý — bude viditelný v ${time} v den příjezdu (${date})`,
    pinLabel: 'PIN',
    pinHiddenUntil: (time, date) => `PIN je momentálně skrytý — bude viditelný v ${time} v den příjezdu (${date})`,
    pinValidNote: 'Platí po celou dobu pobytu · Zadejte na klávesnici dveří',
    lockOfflineNote: 'Chytrý zámek je dočasně nedostupný. Kontaktujte prosím svého hostitele.',
    wifiTitle: 'WiFi',
    networkLabel: 'Síť',
    passwordLabel: 'Heslo',
    parkingTitle: 'Parkování',
    priceLabel: 'Cena',
    contactTitle: 'Kontakt na hostitele',
    nameLabel: 'Jméno',
    phoneLabel: 'Telefon',
    emailLabel: 'E-mail',
    guideTitle: 'Průvodce',
    checkoutTitle: 'Pokyny k odjezdu',
    photosTitle: 'Fotky',
    linksTitle: 'Užitečné odkazy',
    copyLabel: 'Kopírovat',
    copiedLabel: '✓ Zkopírováno',
    showMoreLabel: 'Zobrazit více ▾',
    showLessLabel: 'Zobrazit méně ▴',
    czech: 'Čeština',
    english: 'Angličtina',
  },
  en: {
    headerLabel: 'Check-in Guide',
    managerViewBadge: 'MANAGER VIEW',
    inLabel: 'In',
    outLabel: 'Out',
    statusNotReady: (checkInDate, checkInTime) => `Not ready yet · Check-in from ${checkInDate}${checkInTime ? ' at ' + checkInTime : ''}`,
    statusReady: 'Ready for check-in',
    statusExpired: (checkOutDate) => `Stay completed · Checked out ${checkOutDate}`,
    weatherSectionTitle: 'Weather During Your Stay',
    loadingGuide: 'Loading guide…',
    expiredTitle: 'Stay completed',
    expiredBody: 'This check-in guide is no longer available — your stay has ended.',
    expiredFooter: 'We hope you enjoyed your stay. See you next time!',
    blockedTitle: 'Guide access blocked',
    blockedBody: 'Your host has temporarily blocked access to this check-in guide. Please contact them directly for more information.',
    notReadyTitle: 'Almost there',
    notReadyBody: 'This guide unlocks automatically once your guest registration is complete.',
    finishRegistration: 'Finish registration',
    notFoundTitle: 'Guide not available',
    notFoundBody: 'This check-in guide link is invalid or the guide has not been set up yet.',
    taxStepRegistration: 'Registration',
    taxStepPayment: 'Payment',
    taxStepGuide: 'Guide',
    taxRequiredTitle: 'City Tax',
    taxRequiredBody: 'Before your Check-in Guide is shown, please pay the city tax for your stay.',
    taxDueBannerTitle: 'City tax not paid yet',
    taxDueBannerBody: 'Your host has opened the guide for you early, but the city tax for your stay still needs to be paid.',
    taxPayMissingButton: 'Pay remaining balance →',
    taxAlreadyPaidLabel: 'Already paid',
    taxMissingLabel: 'Remaining balance',
    taxConfirming: 'Confirming payment…',
    taxSummaryError: 'Could not load the amount due. Please try refreshing the page.',
    taxNights: 'nights',
    taxAdults: 'adults',
    taxChildren: 'children',
    taxDisabled: 'disabled',
    taxTotal: 'Total',
    taxPayButton: 'Pay by card →',
    taxPaying: 'Redirecting to payment…',
    taxStripeNotice: 'Payment is processed by Stripe. We don\'t store your payment details. The page unlocks automatically once payment is confirmed — if you already paid, try refreshing.',
    untranslatedNote: 'Some sections are shown in the original language your host wrote them in — an English version isn\'t available yet.',
    payAttentionTitle: 'Pay Attention',
    addressTitle: 'Address',
    openInMaps: 'Open in Maps',
    keyAccessTitle: 'Key Access',
    keyAccessLabels: {
      DOOR_CODE: 'Door Code',
      KEYBOX:    'Key Box',
      PERSON:    'In Person / By Host',
      NONE:      'No Key Needed',
      TTLOCK:    'Smart Lock',
    },
    codeLabel: 'Code',
    codeHiddenUntil: (time, date) => `Code is currently hidden — will be visible at ${time} on check-in day (${date})`,
    pinLabel: 'PIN',
    pinHiddenUntil: (time, date) => `PIN is currently hidden — will be visible at ${time} on check-in day (${date})`,
    pinValidNote: 'Valid for your entire stay · Enter on the door keypad',
    lockOfflineNote: 'Smart lock access is temporarily unavailable. Please contact your host for assistance.',
    wifiTitle: 'WiFi',
    networkLabel: 'Network',
    passwordLabel: 'Password',
    parkingTitle: 'Parking',
    priceLabel: 'Price',
    contactTitle: 'Host Contact',
    nameLabel: 'Name',
    phoneLabel: 'Phone',
    emailLabel: 'Email',
    guideTitle: 'Guide',
    checkoutTitle: 'Check-out Instructions',
    photosTitle: 'Photos',
    linksTitle: 'Useful Links',
    copyLabel: 'Copy',
    copiedLabel: '✓ Copied',
    showMoreLabel: 'Show more ▾',
    showLessLabel: 'Show less ▴',
    czech: 'Czech',
    english: 'English',
  },
};

const WEATHER_LABELS: Record<GuideLanguage, Record<number, string>> = {
  en: {
    0: 'Sunny', 1: 'Mostly sunny', 2: 'Partly cloudy', 3: 'Cloudy', 45: 'Foggy', 48: 'Foggy',
    51: 'Light drizzle', 53: 'Drizzle', 55: 'Dense drizzle', 56: 'Freezing drizzle', 57: 'Freezing drizzle',
    61: 'Light rain', 63: 'Rain', 65: 'Heavy rain', 66: 'Freezing rain', 67: 'Freezing rain',
    71: 'Light snow', 73: 'Snow', 75: 'Heavy snow', 77: 'Snow grains',
    80: 'Rain showers', 81: 'Rain showers', 82: 'Violent showers',
    85: 'Snow showers', 86: 'Snow showers', 95: 'Thunderstorm', 96: 'Thunderstorm', 99: 'Thunderstorm',
  },
  cs: {
    0: 'Slunečno', 1: 'Převážně slunečno', 2: 'Polojasno', 3: 'Zataženo', 45: 'Mlha', 48: 'Mlha',
    51: 'Slabé mrholení', 53: 'Mrholení', 55: 'Husté mrholení', 56: 'Mrznoucí mrholení', 57: 'Mrznoucí mrholení',
    61: 'Slabý déšť', 63: 'Déšť', 65: 'Silný déšť', 66: 'Mrznoucí déšť', 67: 'Mrznoucí déšť',
    71: 'Slabé sněžení', 73: 'Sněžení', 75: 'Silné sněžení', 77: 'Sněhová zrna',
    80: 'Přeháňky', 81: 'Přeháňky', 82: 'Prudké přeháňky',
    85: 'Sněhové přeháňky', 86: 'Sněhové přeháňky', 95: 'Bouřka', 96: 'Bouřka', 99: 'Bouřka',
  },
};

/** SectionNav's sticky bar renders at roughly this height, plus whatever top offset it's using
 *  (see the max(10px, safe-area-inset-top) on its own `top` style) — keeps a jumped-to section's
 *  title clear of it instead of landing hidden underneath. Keep these two in sync. */
const SECTION_NAV_SCROLL_MARGIN = 'calc(max(10px, env(safe-area-inset-top, 0px)) + 62px)';

const Section: React.FC<{ id?: string; icon: string; title: string; children: React.ReactNode }> = ({ id, icon, title, children }) => (
  <div id={id} style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', marginBottom: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', scrollMarginTop: id ? SECTION_NAV_SCROLL_MARGIN : undefined }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <span style={{ fontSize: '1.2rem' }}>{icon}</span>
      <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{title}</span>
    </div>
    {children}
  </div>
);

/** Fade + "Show more" clamp for long free-text blocks (Detailed Guide, Checkout Instructions,
 *  Key Access remark, Address remark). Collapses to ~15 lines with a bottom fade, expands in
 *  place on click. The button/fade only render when the text actually overflows the clamp —
 *  measured via scrollHeight vs clientHeight — so short text renders plainly with no dead
 *  "Show more" control.
 *
 *  Expansion is a controlled prop, not local state — the page keeps a single "which section is
 *  expanded" value so opening one clamp collapses any other, and tapping a nav chip can expand
 *  its target section directly (see CheckinGuidePage's expandedSectionId). */
const ClampText: React.FC<{
  text: string; lines?: number; fontSize?: string; color?: string;
  expanded: boolean; onToggle: () => void; showMoreLabel: string; showLessLabel: string;
}> = ({ text, lines = 15, fontSize = '0.9rem', color = '#334155', expanded, onToggle, showMoreLabel, showLessLabel }) => {
  const [overflows, setOverflows] = useState(false);
  const textRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Every ClampText starts out collapsed (expandedSectionId is null until a guest interacts),
    // so this always measures against the clamped height on first mount, regardless of what
    // later toggles `expanded` to true.
    const el = textRef.current;
    if (!el) return;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <div
          ref={textRef}
          style={{
            fontSize, color, lineHeight: 1.7, whiteSpace: 'pre-wrap',
            maxHeight: expanded ? undefined : `${lines * 1.7}em`, overflow: expanded ? undefined : 'hidden',
          }}
        >
          {text}
        </div>
        {!expanded && overflows && (
          <div style={{
            position: 'absolute', left: 0, right: 0, bottom: 0, height: '2.4em',
            background: 'linear-gradient(to bottom, rgba(255,255,255,0), #fff 75%)',
            pointerEvents: 'none',
          }} />
        )}
      </div>
      {overflows && (
        <button
          onClick={onToggle}
          style={{ display: 'block', marginTop: 6, background: 'none', border: 'none', padding: 0, fontSize: '0.82rem', fontWeight: 700, color: '#2563eb', cursor: 'pointer', fontFamily: 'inherit' }}
        >
          {expanded ? showLessLabel : showMoreLabel}
        </button>
      )}
    </div>
  );
};

interface NavItem { id: string; icon: string; title: string; }

/** Sticky pill nav — one chip per section actually rendered for this guide. Active chip is
 *  tracked via a direct scroll-position geometry check (last section whose top has scrolled
 *  past the nav bar), not IntersectionObserver: the observer's "detection band" approach breaks
 *  right after a programmatic jump, since a short section and the one below it can both report
 *  isIntersecting in the same callback batch, and whichever entry is processed last wins —
 *  not necessarily the one actually on screen. */
const SectionNav: React.FC<{ items: NavItem[]; onNavigate: (id: string) => void }> = ({ items, onNavigate }) => {
  const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null);
  const navRef = React.useRef<HTMLDivElement>(null);
  const chipRefs = React.useRef<Record<string, HTMLAnchorElement | null>>({});
  const itemsKey = items.map(i => i.id).join(',');

  const lastActiveRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (items.length === 0) return;

    const setActiveFromScroll = () => {
      const nav = navRef.current;
      if (!nav) return;
      const threshold = nav.getBoundingClientRect().bottom + 4;
      let current = items[0].id;
      for (const item of items) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= threshold) current = item.id;
      }
      if (current === lastActiveRef.current) return; // no change — skip the re-scroll entirely
      lastActiveRef.current = current;
      setActiveId(current);

      // Scroll the nav's own scrollLeft directly, not chip.scrollIntoView() — scrollIntoView on
      // an element inside a position:sticky container can still nudge the PAGE's vertical scroll
      // as a side effect (the nav has no explicit vertical scroll container of its own), which is
      // exactly what caused the up/down "jumping" bug. Touching only nav.scrollLeft can't do that.
      const chip = chipRefs.current[current];
      if (chip) {
        const target = chip.offsetLeft - (nav.clientWidth - chip.offsetWidth) / 2;
        nav.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
      }
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { setActiveFromScroll(); ticking = false; });
    };
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    setActiveFromScroll();
    return () => document.removeEventListener('scroll', onScroll, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey]);

  if (items.length === 0) return null;

  return (
    <nav
      ref={navRef}
      style={{
        // Sticks flush at the true top (no gap for scrolled content to peek through). The
        // buffer against the status bar/notch — max(): a plain fixed floor, not just the notch
        // inset, since env(safe-area-inset-top) is 0 on many phones (particularly Android) in a
        // normal browser tab even though the OS status bar still ends up flush against the nav
        // once the browser's own address bar collapses on scroll-down — is added as extra
        // top padding instead, so the nav's own background still extends all the way to y:0
        // and only the chip content is pushed down clear of it.
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
        borderBottom: '1px solid #e2e8f0',
        paddingTop: 'calc(max(10px, env(safe-area-inset-top, 0px)) + 12px)',
        paddingBottom: 12, paddingLeft: 12, paddingRight: 12,
        display: 'flex', gap: 8, overflowX: 'auto', marginBottom: 16,
      }}
    >
      {items.map(item => {
        const active = item.id === activeId;
        return (
          <a
            key={item.id}
            ref={el => { chipRefs.current[item.id] = el; }}
            href={`#${item.id}`}
            onClick={e => {
              e.preventDefault();
              document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              onNavigate(item.id);
            }}
            style={{
              flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 6,
              padding: '9px 16px', borderRadius: 999,
              fontSize: '0.85rem', fontWeight: 700,
              background: active ? '#2563eb' : '#eef2f7', color: active ? '#fff' : '#475569',
              textDecoration: 'none', whiteSpace: 'nowrap',
              transition: 'background 0.15s, color 0.15s, transform 0.15s',
              transform: active ? 'scale(1.04)' : undefined,
            }}
          >
            <span style={{ fontSize: '1.05rem' }}>{item.icon}</span>
            {item.title}
          </a>
        );
      })}
    </nav>
  );
};

const Row: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
      <span style={{ fontSize: '0.85rem', color: '#64748b', minWidth: 90, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f172a', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
};

const CopyableRow: React.FC<{ label: string; value: string | null | undefined; copyLabel?: string; copiedLabel?: string }> = ({ label, value, copyLabel = 'Copy', copiedLabel = '✓ Copied' }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
      <span style={{ fontSize: '0.85rem', color: '#64748b', minWidth: 90, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', fontFamily: 'monospace', letterSpacing: '0.06em', background: '#f1f5f9', padding: '3px 10px', borderRadius: 6 }}>{value}</span>
      <button onClick={copy} style={{ fontSize: '0.75rem', padding: '3px 10px', borderRadius: 6, border: '1px solid #cbd5e0', background: copied ? '#dcfce7' : '#f8fafc', color: copied ? '#166534' : '#4a5568', cursor: 'pointer', fontWeight: 600 }}>
        {copied ? copiedLabel : copyLabel}
      </button>
    </div>
  );
};

const LanguageToggle: React.FC<{ language: GuideLanguage; setLanguage: (l: GuideLanguage) => void; t: GuideText; dark?: boolean }> = ({ language, setLanguage, t, dark }) => (
  <div style={{ display: 'flex', gap: 4 }}>
    {(['cs', 'en'] as GuideLanguage[]).map(lang => (
      <button key={lang} type="button" onClick={() => setLanguage(lang)} style={{
        padding: '3px 9px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
        border: dark
          ? (language === lang ? '1.5px solid rgba(255,255,255,0.8)' : '1.5px solid rgba(255,255,255,0.3)')
          : (language === lang ? '1.5px solid #2563eb' : '1.5px solid #cbd5e0'),
        background: dark
          ? (language === lang ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)')
          : (language === lang ? '#eff6ff' : '#fff'),
        color: dark ? '#fff' : (language === lang ? '#1d4ed8' : '#64748b'),
      }}>
        {lang === 'cs' ? '🇨🇿' : '🇬🇧'} {lang === 'cs' ? t.czech : t.english}
      </button>
    ))}
  </div>
);

const StatusBadge: React.FC<{ status: PublicCheckinGuide['status']; checkInDate: string; checkInTime: string | null; checkOutDate: string; t: GuideText }> = ({ status, checkInDate, checkInTime, checkOutDate, t }) => {
  const styles = {
    NOT_READY: { bg: '#fef9c3', border: '#fde047', color: '#854d0e', icon: '🕐' },
    READY:     { bg: '#dcfce7', border: '#86efac', color: '#166534', icon: '✅' },
    EXPIRED:   { bg: '#f1f5f9', border: '#cbd5e0', color: '#64748b', icon: '🔒' },
  };
  const s = styles[status];
  const labels = {
    NOT_READY: t.statusNotReady(checkInDate, checkInTime),
    READY:     t.statusReady,
    EXPIRED:   t.statusExpired(checkOutDate),
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, background: s.bg, border: `1.5px solid ${s.border}`, marginBottom: 16 }}>
      <span style={{ fontSize: '1.3rem' }}>{s.icon}</span>
      <span style={{ fontWeight: 700, fontSize: '0.95rem', color: s.color }}>{labels[status]}</span>
    </div>
  );
};

// ── Weather ──────────────────────────────────────────────────────────────────

/** Open-Meteo WMO weather codes — https://open-meteo.com/en/docs */
const WEATHER_ICON_MAP: Record<number, string> = {
  0: '☀️', 1: '🌤️', 2: '⛅', 3: '☁️', 45: '🌫️', 48: '🌫️',
  51: '🌦️', 53: '🌦️', 55: '🌦️', 56: '🌧️', 57: '🌧️',
  61: '🌧️', 63: '🌧️', 65: '🌧️', 66: '🌧️', 67: '🌧️',
  71: '❄️', 73: '❄️', 75: '❄️', 77: '❄️',
  80: '🌦️', 81: '🌦️', 82: '🌧️',
  85: '🌨️', 86: '🌨️', 95: '⛈️', 96: '⛈️', 99: '⛈️',
};
const getWeatherDisplay = (code: number, lang: GuideLanguage) => ({
  icon: WEATHER_ICON_MAP[code] ?? '🌡️',
  label: WEATHER_LABELS[lang][code] ?? '—',
});

/** "YYYY-MM-DD" parses as UTC midnight per the JS spec — build a local-midnight Date instead
 *  to avoid shifting the displayed day backwards in negative-UTC-offset timezones. */
const parseLocalDate = (isoDate: string): Date => {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const WeatherSection: React.FC<{ days: PublicCheckinGuide['weatherDays']; lang: GuideLanguage; t: GuideText }> = ({ days, lang, t }) => {
  if (!days || days.length === 0) return null;
  return (
    <div style={{
      background: 'linear-gradient(160deg, #eff6ff 0%, #f8fbff 60%, #ffffff 100%)',
      border: '1px solid #dbeafe', borderRadius: 14, padding: '16px 16px 14px', marginBottom: 12,
      boxShadow: '0 1px 6px rgba(30,58,138,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: '1.2rem' }}>🌤️</span>
        <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {t.weatherSectionTitle}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'space-between' }}>
        {days.map(d => {
          const isCheckin = d.label === 'Check-in';
          const w = getWeatherDisplay(d.weatherCode, lang);
          return (
            <div key={d.date} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
              padding: '12px 4px 10px', borderRadius: 12, flex: '1 1 0', minWidth: 0,
              background: isCheckin ? 'linear-gradient(160deg, #1e3a8a 0%, #2563eb 100%)' : 'rgba(255,255,255,0.65)',
              boxShadow: isCheckin ? '0 4px 14px rgba(37,99,235,0.28)' : undefined,
            }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: isCheckin ? 'rgba(255,255,255,0.85)' : '#64748b', marginBottom: 2 }}>
                {d.label}
              </div>
              <div style={{ fontSize: '0.68rem', color: isCheckin ? 'rgba(255,255,255,0.65)' : '#94a3b8', marginBottom: 6 }}>
                {parseLocalDate(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </div>
              <div style={{ fontSize: '1.6rem', lineHeight: 1, marginBottom: 6 }}>{w.icon}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 3 }}>
                <span style={{ fontSize: '0.95rem', fontWeight: 800, color: isCheckin ? '#fff' : '#0f172a' }}>{Math.round(d.tempHigh)}°</span>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: isCheckin ? 'rgba(255,255,255,0.7)' : '#94a3b8' }}>{Math.round(d.tempLow)}°</span>
              </div>
              <div style={{ fontSize: '0.66rem', color: isCheckin ? 'rgba(255,255,255,0.85)' : '#64748b', fontWeight: 600, lineHeight: 1.2 }}>
                {w.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Manager Panel ─────────────────────────────────────────────────────────────

const CLEANING_CONFIG = {
  PENDING: { label: 'Pending', activeBg: '#e2e8f0', activeBorder: '#94a3b8', activeColor: '#475569' },
  READY:   { label: 'Ready',   activeBg: '#dcfce7', activeBorder: '#22c55e', activeColor: '#166534' },
} as const;

const ManagerPanel: React.FC<{
  overlay: ManagerGuideOverlay;
  formToken: string;
  guideCheckInDate: string;
  guideCheckInTime: string | null;
  guideCheckOutDate: string;
  onOverlayChange: (updated: ManagerGuideOverlay) => void;
}> = ({ overlay, formToken, guideCheckInDate, guideCheckInTime, guideCheckOutDate, onOverlayChange }) => {
  const [cleaningUpdating, setCleaningUpdating] = useState(false);
  const [showRegen, setShowRegen] = useState(false);
  const [regenStartDate, setRegenStartDate] = useState(guideCheckInDate);
  const [regenStartTime, setRegenStartTime] = useState('11:00');
  const [regenEndDate, setRegenEndDate] = useState(guideCheckOutDate);
  const [regenEndTime, setRegenEndTime] = useState('11:00');
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenError, setRegenError] = useState('');
  const [visibleFromEdit, setVisibleFromEdit] = useState<string | null>(null); // null = not editing
  const [visibleFromSaving, setVisibleFromSaving] = useState(false);
  const [keyCodeEdit, setKeyCodeEdit] = useState<string | null>(null); // null = not editing
  const [keyCodeSaving, setKeyCodeSaving] = useState(false);
  const [pinManualEdit, setPinManualEdit] = useState<string | null>(null); // null = not editing
  const [pinManualSaving, setPinManualSaving] = useState(false);
  const [offlinePinLoading, setOfflinePinLoading] = useState(false);
  const [offlinePinError, setOfflinePinError] = useState('');
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [offlineStartDate, setOfflineStartDate] = useState(guideCheckInDate);
  const [offlineStartTime, setOfflineStartTime] = useState('11:00');
  const [offlineEndDate, setOfflineEndDate] = useState(guideCheckOutDate);
  const [offlineEndTime, setOfflineEndTime] = useState('11:00');

  const token = localStorage.getItem('token') || '';

  const handleCleaningStatus = async (status: 'PENDING' | 'READY') => {
    if (cleaningUpdating || overlay.cleaningStatus === status) return;
    setCleaningUpdating(true);
    try {
      await updateCleaningStatus(formToken, status, token);
      onOverlayChange({ ...overlay, cleaningStatus: status });
    } catch {
      // silently ignore — user can retry
    } finally {
      setCleaningUpdating(false);
    }
  };

  const handleRegeneratePin = async () => {
    if (!regenStartDate || !regenStartTime || !regenEndDate || !regenEndTime) return;
    setRegenLoading(true);
    setRegenError('');
    try {
      const newPin = await regenerateTtlockPin(formToken, regenStartDate, regenStartTime, regenEndDate, regenEndTime, token);
      onOverlayChange({ ...overlay, ttlockPinCode: newPin });
      setShowRegen(false);
    } catch (err: any) {
      const code = err?.response?.data?.error;
      if (code === 'LOCK_OFFLINE') {
        setRegenError('Lock is currently offline. Try again in a moment.');
      } else {
        setRegenError('PIN generation failed. Please try again later.');
      }
    } finally {
      setRegenLoading(false);
    }
  };

  const handleGetOfflinePin = async () => {
    if (!offlineStartDate || !offlineStartTime || !offlineEndDate || !offlineEndTime) return;
    setOfflinePinLoading(true);
    setOfflinePinError('');
    try {
      const newPin = await getOfflineTtlockPin(formToken, token, offlineStartDate, offlineStartTime, offlineEndDate, offlineEndTime);
      onOverlayChange({ ...overlay, ttlockPinCode: newPin, ttlockPinIsOffline: true, ttlockPinError: null });
      setShowOfflineModal(false);
    } catch (err: any) {
      setOfflinePinError('Failed to get offline code. Please try again later.');
    } finally {
      setOfflinePinLoading(false);
    }
  };

  const handleSaveKeyCode = async (value: string | null) => {
    setKeyCodeSaving(true);
    try {
      await updateKeyCodeOverride(formToken, value, token);
      const newOverride = value && value.trim() ? value.trim() : null;
      onOverlayChange({
        ...overlay,
        keyCodeOverride: newOverride,
        effectiveKeyCode: newOverride ?? overlay.defaultKeyCode,
        keyCode: newOverride ?? overlay.defaultKeyCode,
      });
      setKeyCodeEdit(null);
    } catch {
      // silently ignore — user can retry
    } finally {
      setKeyCodeSaving(false);
    }
  };

  const handleSavePinManual = async (value: string | null) => {
    setPinManualSaving(true);
    try {
      await setTtlockPinManually(formToken, value, token);
      onOverlayChange({ ...overlay, ttlockPinCode: value && value.trim() ? value.trim() : null });
      setPinManualEdit(null);
    } catch {
      // silently ignore — user can retry
    } finally {
      setPinManualSaving(false);
    }
  };

  const handleSaveVisibleFrom = async (value: string | null) => {
    setVisibleFromSaving(true);
    try {
      await updateVisibleFrom(formToken, value, token);
      const newOverride = value && value.trim() ? value.trim() : null;
      onOverlayChange({
        ...overlay,
        visibleFromOverride: newOverride,
        effectiveVisibleFrom: newOverride ?? overlay.defaultVisibleFrom,
      });
      setVisibleFromEdit(null);
    } catch {
      // silently ignore — user can retry
    } finally {
      setVisibleFromSaving(false);
    }
  };

  return (
    <>
      <div style={{ background: '#eef2ff', border: '1.5px solid #818cf8', borderRadius: 12, padding: '14px 16px', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: '1rem' }}>🔧</span>
          <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Manager Controls</span>
        </div>

        {/* Cleaning Status */}
        <div style={{ marginBottom: overlay.ttlockEnabled || overlay.keyCode ? 12 : 0 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Cleaning Status</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(Object.keys(CLEANING_CONFIG) as Array<keyof typeof CLEANING_CONFIG>).map(s => {
              const cfg = CLEANING_CONFIG[s];
              const active = overlay.cleaningStatus === s;
              return (
                <button
                  key={s}
                  onClick={() => handleCleaningStatus(s)}
                  disabled={cleaningUpdating || active}
                  style={{
                    padding: '5px 14px',
                    borderRadius: 6,
                    border: `1.5px solid ${active ? cfg.activeBorder : '#c7d2fe'}`,
                    background: active ? cfg.activeBg : '#f8fafc',
                    color: active ? cfg.activeColor : '#94a3b8',
                    fontWeight: active ? 700 : 500,
                    fontSize: '0.82rem',
                    cursor: active || cleaningUpdating ? 'default' : 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {cfg.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* TTLock PIN — always visible for manager */}
        {overlay.ttlockEnabled && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Smart Lock PIN</div>
            {pinManualEdit === null ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {overlay.ttlockPinCode ? (
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', background: '#e0e7ff', padding: '4px 12px', borderRadius: 6, letterSpacing: '0.1em' }}>
                    {overlay.ttlockPinCode}
                  </span>
                ) : overlay.ttlockPinError === 'OFFLINE' ? (
                  <span style={{ fontSize: '0.82rem', color: '#92400e', background: '#fffbeb', border: '1px solid #f6ad55', borderRadius: 6, padding: '4px 10px' }}>
                    ⚠️ Lock not connected to gateway — PIN generation failed
                  </span>
                ) : (
                  <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>No PIN generated yet</span>
                )}
                {overlay.ttlockPinCode && overlay.ttlockPinIsOffline && (
                  <span title="Generated via cloud-only offline passcode — works without the lock being connected to a gateway" style={{ fontSize: '0.72rem', background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '1px 7px', fontWeight: 700 }}>
                    ⚡ offline
                  </span>
                )}
                <button
                  onClick={() => { setRegenStartDate(guideCheckInDate); setRegenStartTime('11:00'); setRegenEndDate(guideCheckOutDate); setRegenEndTime('11:00'); setRegenError(''); setShowRegen(true); }}
                  style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #818cf8', background: '#fff', color: '#4338ca', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
                >
                  {overlay.ttlockPinCode ? 'Regenerate…' : 'Generate PIN…'}
                </button>
                <button
                  onClick={() => setPinManualEdit(overlay.ttlockPinCode ?? '')}
                  style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #94a3b8', background: '#fff', color: '#475569', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
                >
                  Set manually…
                </button>
                <button
                  onClick={() => { setOfflineStartDate(guideCheckInDate); setOfflineStartTime('11:00'); setOfflineEndDate(guideCheckOutDate); setOfflineEndTime('11:00'); setOfflinePinError(''); setShowOfflineModal(true); }}
                  disabled={offlinePinLoading}
                  title="Get a cloud-only passcode that works even if the lock has no gateway connection"
                  style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #f6ad55', background: '#fff', color: '#92400e', fontWeight: 600, fontSize: '0.8rem', cursor: offlinePinLoading ? 'not-allowed' : 'pointer' }}
                >
                  {offlinePinLoading ? 'Getting…' : '⚡ Get offline code…'}
                </button>
                {offlinePinError && (
                  <span style={{ fontSize: '0.78rem', color: '#dc2626' }}>{offlinePinError}</span>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={pinManualEdit}
                  onChange={e => setPinManualEdit(e.target.value)}
                  placeholder="Enter PIN…"
                  style={{ padding: '5px 8px', border: '1px solid #818cf8', borderRadius: 6, fontSize: '0.9rem', fontFamily: 'monospace', width: 120 }}
                />
                <button
                  onClick={() => handleSavePinManual(pinManualEdit)}
                  disabled={pinManualSaving}
                  style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#4f46e5', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: pinManualSaving ? 'not-allowed' : 'pointer' }}
                >
                  {pinManualSaving ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => setPinManualEdit(null)}
                  disabled={pinManualSaving}
                  style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>No TTLock programming — display only</span>
              </div>
            )}
          </div>
        )}

        {/* Key Code — editable per-reservation override */}
        {(!overlay.ttlockEnabled) && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Key Code</div>
            {keyCodeEdit === null ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {overlay.effectiveKeyCode ? (
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', background: '#e0e7ff', padding: '4px 12px', borderRadius: 6, letterSpacing: '0.1em' }}>
                    {overlay.effectiveKeyCode}
                  </span>
                ) : (
                  <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>No code set</span>
                )}
                {overlay.keyCodeOverride && (
                  <span style={{ fontSize: '0.72rem', background: '#e0e7ff', color: '#4338ca', borderRadius: 4, padding: '1px 7px', fontWeight: 700 }}>override</span>
                )}
                <button
                  onClick={() => setKeyCodeEdit(overlay.keyCodeOverride ?? overlay.defaultKeyCode ?? '')}
                  style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #818cf8', background: '#fff', color: '#4338ca', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}
                >
                  Edit
                </button>
                {overlay.keyCodeOverride && (
                  <button
                    onClick={() => handleSaveKeyCode(null)}
                    disabled={keyCodeSaving}
                    style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}
                  >
                    Clear override
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={keyCodeEdit}
                  onChange={e => setKeyCodeEdit(e.target.value)}
                  placeholder="Enter code…"
                  style={{ padding: '5px 8px', border: '1px solid #818cf8', borderRadius: 6, fontSize: '0.9rem', fontFamily: 'monospace', width: 140 }}
                />
                <button
                  onClick={() => handleSaveKeyCode(keyCodeEdit)}
                  disabled={keyCodeSaving}
                  style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#4f46e5', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: keyCodeSaving ? 'not-allowed' : 'pointer' }}
                >
                  {keyCodeSaving ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => setKeyCodeEdit(null)}
                  disabled={keyCodeSaving}
                  style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                {overlay.defaultKeyCode && (
                  <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>apt. default: {overlay.defaultKeyCode}</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Code Reveal Time — per-reservation override */}
        <div style={{ borderTop: '1px solid #c7d2fe', paddingTop: 10 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Code Reveal Time</div>
          {visibleFromEdit === null ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b' }}>
                {overlay.effectiveVisibleFrom
                  ? `Visible from ${overlay.effectiveVisibleFrom}`
                  : 'Always visible'}
              </span>
              {overlay.visibleFromOverride && (
                <span style={{ fontSize: '0.72rem', background: '#e0e7ff', color: '#4338ca', borderRadius: 4, padding: '1px 7px', fontWeight: 700 }}>override</span>
              )}
              {!overlay.visibleFromOverride && overlay.defaultVisibleFrom && (
                <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>apt. default</span>
              )}
              <button
                onClick={() => setVisibleFromEdit(overlay.visibleFromOverride ?? overlay.defaultVisibleFrom ?? '')}
                style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #818cf8', background: '#fff', color: '#4338ca', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}
              >
                Edit
              </button>
              {overlay.visibleFromOverride && (
                <button
                  onClick={() => handleSaveVisibleFrom(null)}
                  disabled={visibleFromSaving}
                  style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}
                >
                  Clear override
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <input
                type="time"
                value={visibleFromEdit}
                onChange={e => setVisibleFromEdit(e.target.value)}
                style={{ padding: '5px 8px', border: '1px solid #818cf8', borderRadius: 6, fontSize: '0.9rem', fontFamily: 'monospace' }}
              />
              <button
                onClick={() => handleSaveVisibleFrom(visibleFromEdit)}
                disabled={visibleFromSaving || !visibleFromEdit}
                style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#4f46e5', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: visibleFromSaving ? 'not-allowed' : 'pointer' }}
              >
                {visibleFromSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => setVisibleFromEdit(null)}
                disabled={visibleFromSaving}
                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
              {overlay.defaultVisibleFrom && (
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>apt. default: {overlay.defaultVisibleFrom}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Regenerate PIN modal */}
      {showRegen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowRegen(false)}
        >
          <div
            style={{ background: '#fff', borderRadius: 14, padding: '22px 24px', maxWidth: 380, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b', marginBottom: 4 }}>
              {overlay.ttlockPinCode ? 'Regenerate Smart Lock PIN' : 'Generate Smart Lock PIN'}
            </div>
            <div style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 16 }}>
              Set a custom start and end time — useful for early luggage drop-off, early check-in, or a late checkout.
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Start Date</label>
                <input
                  type="date"
                  value={regenStartDate}
                  onChange={e => setRegenStartDate(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Start Time</label>
                <input
                  type="time"
                  value={regenStartTime}
                  onChange={e => setRegenStartTime(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>End Date</label>
                <input
                  type="date"
                  value={regenEndDate}
                  onChange={e => setRegenEndDate(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>End Time</label>
                <input
                  type="time"
                  value={regenEndTime}
                  onChange={e => setRegenEndTime(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            {regenError && (
              <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, color: '#dc2626', fontSize: '0.82rem', marginBottom: 12 }}>
                {regenError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRegen(false)}
                disabled={regenLoading}
                style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleRegeneratePin}
                disabled={regenLoading || !regenStartDate || !regenStartTime || !regenEndDate || !regenEndTime}
                style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: regenLoading ? '#a5b4fc' : '#4f46e5', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: regenLoading ? 'not-allowed' : 'pointer' }}
              >
                {regenLoading ? 'Generating…' : 'Regenerate PIN'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offline PIN modal */}
      {showOfflineModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowOfflineModal(false)}
        >
          <div
            style={{ background: '#fff', borderRadius: 14, padding: '22px 24px', maxWidth: 380, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b', marginBottom: 4 }}>
              Get Offline Passcode
            </div>
            <div style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 16 }}>
              A cloud-only passcode that works even if the lock has no gateway connection. Set a custom start and end time — useful for early luggage drop-off, early check-in, or a late checkout.
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Start Date</label>
                <input
                  type="date"
                  value={offlineStartDate}
                  onChange={e => setOfflineStartDate(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Start Time</label>
                <input
                  type="time"
                  value={offlineStartTime}
                  onChange={e => setOfflineStartTime(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>End Date</label>
                <input
                  type="date"
                  value={offlineEndDate}
                  onChange={e => setOfflineEndDate(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>End Time</label>
                <input
                  type="time"
                  value={offlineEndTime}
                  onChange={e => setOfflineEndTime(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            {offlinePinError && (
              <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, color: '#dc2626', fontSize: '0.82rem', marginBottom: 12 }}>
                {offlinePinError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowOfflineModal(false)}
                disabled={offlinePinLoading}
                style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleGetOfflinePin}
                disabled={offlinePinLoading || !offlineStartDate || !offlineStartTime || !offlineEndDate || !offlineEndTime}
                style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: offlinePinLoading ? '#fbd38d' : '#f6ad55', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: offlinePinLoading ? 'not-allowed' : 'pointer' }}
              >
                {offlinePinLoading ? 'Getting…' : 'Get Offline Code'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── Guide view (shared by the guest page and the manager's preview) ───────────

export interface GuideViewProps {
  guide: PublicCheckinGuide;
  language: GuideLanguage;
  setLanguage: (l: GuideLanguage) => void;
  /** Present only for the real guest page when the visitor owns the apartment. */
  managerOverlay?: ManagerGuideOverlay | null;
  onOverlayChange?: (o: ManagerGuideOverlay) => void;
  /** Real reservation token. Absent in preview: nothing here may talk to the server. */
  formToken?: string;
  /** Manager preview of unsaved settings: no deposit / upsell / manager panels, labelled as preview. */
  preview?: boolean;
  /** City tax still owed on a force-opened guide (#154). Owned by the page, which holds the payment state; never set in preview. */
  taxBanner?: React.ReactNode;
}

export const GuideView: React.FC<GuideViewProps> = ({ guide, language, setLanguage, managerOverlay, onOverlayChange, formToken, preview, taxBanner }) => {
  const t = GUIDE_TEXT[language];
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  // Which section's ClampText (if any) is expanded — only one at a time. Tapping a nav chip sets
  // this directly to that section's id (see SectionNav's onNavigate), landing on it pre-expanded;
  // toggling a "Show more" button flips between that section and null via toggleClampExpanded.
  const [expandedSectionId, setExpandedSectionId] = useState<string | null>(null);
  const toggleClampExpanded = (sectionId: string) => {
    setExpandedSectionId(prev => (prev === sectionId ? null : sectionId));
  };

  useEffect(() => {
    if (lightboxIndex === null || !guide.photos) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); setLightboxIndex(null); }
      if (e.key === 'ArrowLeft' && lightboxIndex > 0) setLightboxIndex(lightboxIndex - 1);
      if (e.key === 'ArrowRight' && lightboxIndex < guide.photos.length - 1) setLightboxIndex(lightboxIndex + 1);
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [lightboxIndex, guide]);

  const hasKey = guide.keyAccessType && guide.keyAccessType !== 'NONE';
  const hasWifi = guide.wifiName || guide.wifiPassword;
  const hasParking = guide.parkingInfo || guide.parkingPrice;
  const hasContact = guide.hostContactName || guide.hostContactPhone || guide.hostContactEmail;
  const hasLinks = guide.otherLinks && guide.otherLinks.length > 0;
  const hasPhotos = guide.photos && guide.photos.length > 0;

  // Mirrors each section's own render condition below — a nav chip only ever appears for a
  // section that will actually render, never a dead link.
  const showAddressSection = guide.showAddress && !!(guide.address || guide.addressRemark);
  const showKeySection = !!((guide.showKeyAccess && guide.keyAccessType) || guide.ttlockPinCode || guide.ttlockPinHidden || guide.ttlockCodeUnavailable);
  const showWifiSection = guide.showWifi && !!hasWifi;
  const showParkingSection = guide.showParking && !!hasParking;
  const showContactSection = guide.showContact && !!hasContact;
  const showGuideSection = guide.showDetailedGuide && !!guide.detailedGuide;
  const showCheckoutSection = guide.showCheckoutInstructions && !!guide.checkoutInstructions;
  const showPhotosSection = guide.showPhotos && !!hasPhotos;
  const showLinksSection = guide.showOtherLinks && !!hasLinks;

  const navItems: NavItem[] = [
    showAddressSection && { id: 'address', icon: '📍', title: t.addressTitle },
    showKeySection && { id: 'key-access', icon: '🔑', title: t.keyAccessTitle },
    showWifiSection && { id: 'wifi', icon: '📶', title: t.wifiTitle },
    showParkingSection && { id: 'parking', icon: '🚗', title: t.parkingTitle },
    showContactSection && { id: 'contact', icon: '📞', title: t.contactTitle },
    showGuideSection && { id: 'guide', icon: '📖', title: t.guideTitle },
    showCheckoutSection && { id: 'checkout', icon: '🚪', title: t.checkoutTitle },
    showPhotosSection && { id: 'photos', icon: '🖼️', title: t.photosTitle },
    showLinksSection && { id: 'links', icon: '🔗', title: t.linksTitle },
  ].filter((item): item is NavItem => !!item);

  return (
    <>
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '0 0 60px' }}>

      {/* Header */}
      <div style={{ position: 'relative', background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)', padding: '24px 20px 28px', marginBottom: 16, borderRadius: '0 0 20px 20px', color: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
            </svg>
          </div>
          <span style={{ fontWeight: 800, fontSize: '1rem' }}>Guestbook<span style={{ color: '#93c5fd' }}>.cz</span></span>
          <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
            <LanguageToggle language={language} setLanguage={setLanguage} t={t} dark />
          </div>
        </div>
        <div style={{ fontSize: '0.8rem', opacity: 0.75, marginBottom: 4 }}>{t.headerLabel}</div>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800 }}>{guide.apartmentName}</h1>
        <div style={{ marginTop: 8, fontSize: '0.85rem', opacity: 0.85 }}>
          {guide.checkInDate} → {guide.checkOutDate}
          {guide.checkInTime && <span> · {t.inLabel}: {guide.checkInTime}</span>}
          {guide.checkOutTime && <span> · {t.outLabel}: {guide.checkOutTime}</span>}
        </div>
        {/* Manager View badge — pinned to the bottom-right corner, below check-in/check-out,
            rather than crammed into the top row where "POHLED SPRÁVCE" (Czech) was wide enough
            to push the row into horizontal overflow on narrow phones. */}
        {managerOverlay && (
          <span style={{
            position: 'absolute', right: 20, bottom: 10,
            fontSize: '0.68rem', fontWeight: 700, background: 'rgba(255,255,255,0.2)',
            borderRadius: 4, padding: '2px 8px', letterSpacing: '0.04em', whiteSpace: 'nowrap',
          }}>
            {t.managerViewBadge}
          </span>
        )}
      </div>

      <SectionNav items={navItems} onNavigate={setExpandedSectionId} />

      <div style={{ padding: '0 12px' }}>

        {taxBanner}

        <StatusBadge status={guide.status} checkInDate={guide.checkInDate} checkInTime={guide.checkInTime} checkOutDate={guide.checkOutDate} t={t} />

        {/* Manager panel — only shown to the apartment owner */}
        {managerOverlay && formToken && onOverlayChange && (
          <ManagerPanel
            overlay={managerOverlay}
            formToken={formToken}
            guideCheckInDate={guide.checkInDate}
            guideCheckInTime={guide.checkInTime}
            guideCheckOutDate={guide.checkOutDate}
            onOverlayChange={onOverlayChange}
          />
        )}

        {/* Untranslated-content notice */}
        {guide.contentPartiallyUntranslated && (
          <div style={{ display: 'flex', gap: 10, padding: '10px 14px', borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0', marginBottom: 12 }}>
            <span style={{ fontSize: '1rem', flexShrink: 0 }}>ℹ️</span>
            <div style={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.5 }}>{t.untranslatedNote}</div>
          </div>
        )}

        {/* Pay Attention */}
        {guide.showAttentionNotes && guide.attentionNotes && (
          <div style={{ padding: '14px 16px', borderRadius: 12, background: '#fff7ed', border: '1.5px solid #fed7aa', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: '1.1rem', flexShrink: 0, lineHeight: 1 }}>⚠️</span>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#9a3412', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.payAttentionTitle}</div>
            </div>
            <div style={{ fontSize: '0.9rem', color: '#7c2d12', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{guide.attentionNotes}</div>
          </div>
        )}

        {/* Early check-in / late check-out. Near the top so it is actually seen, but collapsed —
            a guest who opened the guide for the door code should not have to scroll past a price
            list to reach it. Renders nothing unless the apartment sells them, and opens itself
            when this guest already has a request in flight. */}
        {!preview && formToken && (
          <LanguageOverride lang={language}>
            <DepositGuestSection formToken={formToken} variant="guide" />
            <StayExtensionGuestSection formToken={formToken} />
          </LanguageOverride>
        )}

        {/* Weather */}
        {guide.showWeather && <WeatherSection days={guide.weatherDays} lang={language} t={t} />}

        {/* Address */}
        {guide.showAddress && (guide.address || guide.addressRemark) && (
          <Section id="address" icon="📍" title={t.addressTitle}>
            {guide.address && <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: 4 }}>{guide.address}</div>}
            {guide.addressRemark && (
              <ClampText
                text={guide.addressRemark} fontSize="0.88rem" color="#475569"
                expanded={expandedSectionId === 'address'} onToggle={() => toggleClampExpanded('address')}
                showMoreLabel={t.showMoreLabel} showLessLabel={t.showLessLabel}
              />
            )}
            {guide.address && (
              <a href={`https://maps.google.com/?q=${encodeURIComponent(guide.address)}`} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8, fontSize: '0.82rem', color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>
                🗺 {t.openInMaps}
              </a>
            )}
          </Section>
        )}

        {/* Key Access */}
        {((guide.showKeyAccess && guide.keyAccessType) || guide.ttlockPinCode || guide.ttlockPinHidden || guide.ttlockCodeUnavailable) && (
          <Section id="key-access" icon="🔑" title={t.keyAccessTitle}>
            {guide.keyAccessType && (
              <div style={{ marginBottom: 8, fontSize: '0.88rem', color: '#475569', fontWeight: 600 }}>
                {t.keyAccessLabels[guide.keyAccessType] ?? guide.keyAccessType}
              </div>
            )}
            {hasKey && (
              guide.keyCodeHidden ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#fef9c3', border: '1.5px solid #fde047', marginBottom: 6 }}>
                  <span style={{ fontSize: '1.1rem' }}>🔒</span>
                  <span style={{ fontSize: '0.88rem', color: '#854d0e', fontWeight: 600 }}>
                    {t.codeHiddenUntil(guide.keyCodeVisibleFromTime || '', guide.checkInDate)}
                  </span>
                </div>
              ) : (
                <CopyableRow label={t.codeLabel} value={guide.keyCode} copyLabel={t.copyLabel} copiedLabel={t.copiedLabel} />
              )
            )}
            {/* Deliberately not one of the "hidden" banners above: those mean *not yet* and are
                meant to be waited out. This means *act now*, and the deposit panel at the top of
                the guide carries the button. */}
            {guide.depositBlocksKey && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#fffbeb', border: '1.5px solid #fcd34d', marginBottom: 6 }}>
                <span style={{ fontSize: '1.1rem' }}>⚠</span>
                <span style={{ fontSize: '0.88rem', color: '#92400e', fontWeight: 600 }}>
                  {depositText[language].guestFailedBody}
                </span>
              </div>
            )}
            {guide.ttlockCodeUnavailable && !guide.ttlockPinCode && !guide.ttlockPinHidden && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#f8fafc', border: '1.5px solid #cbd5e1', marginBottom: 6 }}>
                <span style={{ fontSize: '1.1rem' }}>🔒</span>
                <span style={{ fontSize: '0.88rem', color: '#475569' }}>
                  {t.lockOfflineNote}
                </span>
              </div>
            )}
            {(guide.ttlockPinCode || guide.ttlockPinHidden) && (
              <div style={{ marginTop: hasKey ? 10 : 0 }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{t.pinLabel}</div>
                {guide.ttlockPinHidden ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#fef9c3', border: '1.5px solid #fde047', marginBottom: 6 }}>
                    <span style={{ fontSize: '1.1rem' }}>🔒</span>
                    <span style={{ fontSize: '0.88rem', color: '#854d0e', fontWeight: 600 }}>
                      {t.pinHiddenUntil(guide.ttlockPinVisibleFromTime || '', guide.checkInDate)}
                    </span>
                  </div>
                ) : (
                  <>
                    <CopyableRow label={t.pinLabel} value={guide.ttlockPinCode} copyLabel={t.copyLabel} copiedLabel={t.copiedLabel} />
                    <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>
                      {t.pinValidNote}
                    </div>
                  </>
                )}
              </div>
            )}
            {guide.keyRemark && (
              <div style={{ marginTop: 4 }}>
                <ClampText
                  text={guide.keyRemark} fontSize="0.88rem" color="#475569"
                  expanded={expandedSectionId === 'key-access'} onToggle={() => toggleClampExpanded('key-access')}
                  showMoreLabel={t.showMoreLabel} showLessLabel={t.showLessLabel}
                />
              </div>
            )}
          </Section>
        )}

        {/* WiFi */}
        {guide.showWifi && hasWifi && (
          <Section id="wifi" icon="📶" title={t.wifiTitle}>
            <CopyableRow label={t.networkLabel} value={guide.wifiName} copyLabel={t.copyLabel} copiedLabel={t.copiedLabel} />
            <CopyableRow label={t.passwordLabel} value={guide.wifiPassword} copyLabel={t.copyLabel} copiedLabel={t.copiedLabel} />
          </Section>
        )}

        {/* Parking */}
        {guide.showParking && hasParking && (
          <Section id="parking" icon="🚗" title={t.parkingTitle}>
            {guide.parkingInfo && <div style={{ fontSize: '0.9rem', color: '#334155', whiteSpace: 'pre-wrap', marginBottom: 6 }}>{guide.parkingInfo}</div>}
            <Row label={t.priceLabel} value={guide.parkingPrice} />
          </Section>
        )}

        {/* Contact */}
        {guide.showContact && hasContact && (
          <Section id="contact" icon="📞" title={t.contactTitle}>
            <Row label={t.nameLabel} value={guide.hostContactName} />
            {guide.hostContactPhone && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.85rem', color: '#64748b', minWidth: 90 }}>{t.phoneLabel}</span>
                <a href={`tel:${guide.hostContactPhone}`} style={{ fontSize: '0.9rem', fontWeight: 600, color: '#2563eb', textDecoration: 'none' }}>{guide.hostContactPhone}</a>
              </div>
            )}
            {guide.hostContactEmail && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.85rem', color: '#64748b', minWidth: 90 }}>{t.emailLabel}</span>
                <a href={`mailto:${guide.hostContactEmail}`} style={{ fontSize: '0.9rem', fontWeight: 600, color: '#2563eb', textDecoration: 'none' }}>{guide.hostContactEmail}</a>
              </div>
            )}
          </Section>
        )}

        {/* Detailed Guide */}
        {guide.showDetailedGuide && guide.detailedGuide && (
          <Section id="guide" icon="📖" title={t.guideTitle}>
            <ClampText
              text={guide.detailedGuide}
              expanded={expandedSectionId === 'guide'} onToggle={() => toggleClampExpanded('guide')}
              showMoreLabel={t.showMoreLabel} showLessLabel={t.showLessLabel}
            />
          </Section>
        )}

        {/* Check-out Instructions */}
        {guide.showCheckoutInstructions && guide.checkoutInstructions && (
          <Section id="checkout" icon="🚪" title={t.checkoutTitle}>
            <ClampText
              text={guide.checkoutInstructions}
              expanded={expandedSectionId === 'checkout'} onToggle={() => toggleClampExpanded('checkout')}
              showMoreLabel={t.showMoreLabel} showLessLabel={t.showLessLabel}
            />
          </Section>
        )}


        {/* Photos */}
        {guide.showPhotos && hasPhotos && (
          <Section id="photos" icon="🖼️" title={t.photosTitle}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
              {guide.photos.map((photo, idx) => (
                <div key={photo.id} onClick={() => setLightboxIndex(idx)}
                  style={{ cursor: 'pointer', borderRadius: 8, overflow: 'hidden', aspectRatio: '1', background: '#f1f5f9', position: 'relative' }}>
                  <img src={photo.publicUrl} alt={photo.caption || ''}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Other Links */}
        {guide.showOtherLinks && hasLinks && (
          <Section id="links" icon="🔗" title={t.linksTitle}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {guide.otherLinks.map((link, i) => (
                <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, textDecoration: 'none', color: '#0369a1', fontWeight: 600, fontSize: '0.9rem' }}>
                  <span style={{ flex: 1 }}>{link.label || link.url}</span>
                  <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>→</span>
                </a>
              ))}
            </div>
          </Section>
        )}

      </div>
    </div>

    {/* Lightbox */}
    {lightboxIndex !== null && guide.photos && (
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onClick={() => setLightboxIndex(null)}
      >
        <button
          onClick={e => { e.stopPropagation(); setLightboxIndex(null); }}
          style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: '50%', width: 40, height: 40, fontSize: '1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >✕</button>

        {lightboxIndex > 0 && (
          <button
            onClick={e => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }}
            style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: '50%', width: 44, height: 44, fontSize: '1.6rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >‹</button>
        )}

        {lightboxIndex < guide.photos.length - 1 && (
          <button
            onClick={e => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }}
            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: '50%', width: 44, height: 44, fontSize: '1.6rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >›</button>
        )}

        <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '90vw', maxHeight: '90vh' }}>
          <img
            src={guide.photos[lightboxIndex].publicUrl}
            alt={guide.photos[lightboxIndex].caption || ''}
            style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8 }}
          />
          {guide.photos[lightboxIndex].caption && (
            <div style={{ marginTop: 12, color: '#e2e8f0', fontSize: '0.9rem', textAlign: 'center', maxWidth: 420, padding: '0 16px' }}>
              {guide.photos[lightboxIndex].caption}
            </div>
          )}
          <div style={{ marginTop: 8, color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
            {lightboxIndex + 1} / {guide.photos.length}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const CheckinGuidePage: React.FC = () => {
  const { formToken } = useParams<{ formToken: string }>();
  const [guide, setGuide] = useState<PublicCheckinGuide | null>(null);
  const [managerOverlay, setManagerOverlay] = useState<ManagerGuideOverlay | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [expired, setExpired] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [notReady, setNotReady] = useState(false);
  const [taxRequired, setTaxRequired] = useState(false);
  const [taxSummary, setTaxSummary] = useState<PublicCityTaxSummary | null>(null);
  const [taxSummaryError, setTaxSummaryError] = useState(false);
  const [payingTax, setPayingTax] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [language, setLanguage] = useState<GuideLanguage>(() => {
    const stored = localStorage.getItem(GUIDE_LANGUAGE_STORAGE_KEY);
    if (stored === 'cs' || stored === 'en') return stored;
    return window.navigator.language?.toLowerCase().startsWith('cs') ? 'cs' : 'en';
  });
  const t = GUIDE_TEXT[language];

  useEffect(() => {
    localStorage.setItem(GUIDE_LANGUAGE_STORAGE_KEY, language);
  }, [language]);

  // Opt into viewport-fit=cover just while this page is mounted, restoring the app's default
  // afterwards — needed so the sticky section nav's safe-area padding (below) actually reserves
  // space instead of being a no-op. Without this, iOS Safari can render the sticky nav underneath
  // the notch/status-bar area during the browser's address-bar-collapse-on-scroll-down animation,
  // which is exactly the "nav creeps up and gets covered" symptom this fixes.
  useEffect(() => {
    const metaViewport = document.querySelector('meta[name="viewport"]');
    const previousContent = metaViewport?.getAttribute('content') ?? null;
    metaViewport?.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover');
    return () => {
      if (previousContent !== null) metaViewport?.setAttribute('content', previousContent);
    };
  }, []);

  useEffect(() => {
    if (!formToken) return;
    setLoading(true);
    getPublicCheckinGuide(formToken, language)
      .then(data => {
        setGuide(data);
        // After public data loads, silently try the manager overlay.
        // A 403/404 just means the visitor is not the owner — no panel shown.
        const jwt = localStorage.getItem('token');
        if (jwt) {
          getManagerGuideOverlay(formToken, jwt)
            .then(setManagerOverlay)
            .catch(() => {});
        }
      })
      .catch((err: any) => {
        const status = err?.response?.status;
        if (status === 410) {
          setExpired(true);
        } else if (status === 403) {
          setBlocked(true);
        } else if (status === 425) {
          setNotReady(true);
        } else if (status === 402) {
          setTaxRequired(true);
          getPublicCityTaxSummary(formToken)
            .then(setTaxSummary)
            .catch(() => setTaxSummaryError(true));
        } else {
          setNotFound(true);
        }
      })
      .finally(() => setLoading(false));
  }, [formToken, language]);

  // Returning from Stripe Checkout (?payment=success) — the webhook usually confirms within a
  // second or two, but isn't guaranteed to have landed by the time the browser redirects back.
  // Poll briefly before falling back to the normal payment-required screen with a retry hint.
  useEffect(() => {
    if (!formToken) return;
    if (new URLSearchParams(window.location.search).get('payment') !== 'success') return;

    setConfirmingPayment(true);
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;

    const poll = () => {
      if (cancelled) return;
      attempts += 1;
      getPublicCheckinGuide(formToken, language)
        .then(data => {
          if (cancelled) return;
          setGuide(data);
          // A force-opened guide answers 200 even while the tax is unpaid, so a 200 no longer
          // proves the payment landed — keep polling until the tax is no longer due.
          if (data.cityTaxDue && attempts < maxAttempts) {
            setTimeout(poll, 2000);
            return;
          }
          setTaxRequired(false);
          setConfirmingPayment(false);
        })
        .catch(() => {
          if (cancelled) return;
          if (attempts >= maxAttempts) {
            setConfirmingPayment(false);
            return;
          }
          setTimeout(poll, 2000);
        });
    };
    poll();

    return () => { cancelled = true; };
    // Intentionally only depends on formToken — re-runs would restart polling on unrelated state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formToken]);

  // A force-opened guide (#154) has no 402 screen to load the amount, so fetch it for the banner.
  const cityTaxDue = !!guide?.cityTaxDue && !managerOverlay;
  useEffect(() => {
    if (!cityTaxDue || !formToken) return;
    getPublicCityTaxSummary(formToken)
      .then(setTaxSummary)
      .catch(() => setTaxSummaryError(true));
  }, [cityTaxDue, formToken]);

  const handlePayTax = async () => {
    if (!formToken) return;
    setPayingTax(true);
    try {
      const { url } = await createCityTaxCheckoutSession(formToken);
      window.location.href = url;
    } catch {
      setPayingTax(false);
    }
  };

  const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: '#64748b' }}>
        {t.loadingGuide}
      </div>
    );
  }

  if (expired) {
    return (
      <div style={{ maxWidth: 480, margin: '40px auto 0', textAlign: 'center', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <LanguageToggle language={language} setLanguage={setLanguage} t={t} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', color: '#64748b', marginBottom: 16 }}>
          <KeyIcon size={48} color="currentColor" />
        </div>
        <h2 style={{ color: '#1e293b', marginBottom: 8 }}>{t.expiredTitle}</h2>
        <p style={{ color: '#64748b', lineHeight: 1.6 }}>
          {t.expiredBody}
        </p>

        {/* The guide has closed, but money can still be held for days. This is the only thing
            left on the page, and it comes from its own endpoint that outlives the guide. */}
        {formToken && (
          <div style={{ marginTop: 24, textAlign: 'left' }}>
            <LanguageOverride lang={language}>
              <DepositGuestSection formToken={formToken} variant="closed" />
            </LanguageOverride>
          </div>
        )}
        <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: 12 }}>
          {t.expiredFooter}
        </p>
      </div>
    );
  }

  if (blocked) {
    return (
      <div style={{ maxWidth: 480, margin: '40px auto 0', textAlign: 'center', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <LanguageToggle language={language} setLanguage={setLanguage} t={t} />
        </div>
        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🚫</div>
        <h2 style={{ color: '#1e293b', marginBottom: 8 }}>{t.blockedTitle}</h2>
        <p style={{ color: '#64748b', lineHeight: 1.6 }}>
          {t.blockedBody}
        </p>
      </div>
    );
  }

  if (notReady) {
    return (
      <div style={{ maxWidth: 480, margin: '40px auto 0', textAlign: 'center', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <LanguageToggle language={language} setLanguage={setLanguage} t={t} />
        </div>
        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🕐</div>
        <h2 style={{ color: '#1e293b', marginBottom: 8 }}>{t.notReadyTitle}</h2>
        <p style={{ color: '#64748b', lineHeight: 1.6 }}>
          {t.notReadyBody}
        </p>
        {formToken && (
          <a href={`/check-in/form/${formToken}`} style={{
            display: 'inline-block', marginTop: 16, padding: '10px 20px', borderRadius: 10,
            background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)', color: '#fff',
            textDecoration: 'none', fontWeight: 700, fontSize: '0.9rem',
          }}>
            {t.finishRegistration}
          </a>
        )}
      </div>
    );
  }

  if (taxRequired) {
    const s = taxSummary;

    return (
      <div style={{ maxWidth: 480, margin: '40px auto 0', textAlign: 'center', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <LanguageToggle language={language} setLanguage={setLanguage} t={t} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#16a34a', color: '#fff', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>✓</div>
            <span style={{ fontSize: '0.68rem', color: '#16a34a', fontWeight: 600 }}>{t.taxStepRegistration}</span>
          </div>
          <div style={{ width: 18, height: 2, background: '#cbd5e0' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#2563eb', color: '#fff', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>2</div>
            <span style={{ fontSize: '0.68rem', color: '#2563eb', fontWeight: 700 }}>{t.taxStepPayment}</span>
          </div>
          <div style={{ width: 18, height: 2, background: '#cbd5e0' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#e2e8f0', color: '#94a3b8', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>🔒</div>
            <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{t.taxStepGuide}</span>
          </div>
        </div>

        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🧾</div>
        <h2 style={{ color: '#1e293b', marginBottom: 8 }}>{t.taxRequiredTitle}</h2>
        <p style={{ color: '#64748b', lineHeight: 1.6, marginBottom: 20 }}>{t.taxRequiredBody}</p>

        {confirmingPayment ? (
          <p style={{ color: '#2563eb', fontWeight: 600 }}>{t.taxConfirming}</p>
        ) : taxSummaryError || !s ? (
          <p style={{ color: '#dc2626' }}>{t.taxSummaryError}</p>
        ) : (
          <>
            <div style={{ background: '#fff', borderRadius: 12, padding: '16px 16px 14px', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', marginBottom: 16, textAlign: 'left' }}>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: 10 }}>
                {s.apartmentName} · {s.checkInDate}–{s.checkOutDate} ({s.nights} {t.taxNights})
              </div>
              {s.adultCount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#475569', marginBottom: 6 }}>
                  <span>{s.adultCount} {t.taxAdults} × {s.nights} × {fmt(s.adultRate)} {s.currency}</span>
                  <span>{fmt(s.adultSubtotal)} {s.currency}</span>
                </div>
              )}
              {s.childCount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#475569', marginBottom: 6 }}>
                  <span>{s.childCount} {t.taxChildren} × {s.nights} × {fmt(s.childRate)} {s.currency}</span>
                  <span>{fmt(s.childSubtotal)} {s.currency}</span>
                </div>
              )}
              {s.disabledCount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#475569', marginBottom: 6 }}>
                  <span>{s.disabledCount} {t.taxDisabled} × {s.nights} × {fmt(s.disabledRate)} {s.currency}</span>
                  <span>{fmt(s.disabledSubtotal)} {s.currency}</span>
                </div>
              )}
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 10, marginTop: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>{t.taxTotal}</span>
                <span style={{ fontWeight: 800, color: '#2563eb', fontSize: '1.25rem' }}>{fmt(s.amount)} {s.currency}</span>
              </div>
            </div>

            <button
              onClick={handlePayTax}
              disabled={payingTax}
              style={{
                display: 'block', width: '100%', border: 'none', cursor: payingTax ? 'not-allowed' : 'pointer',
                padding: '13px', borderRadius: 10, fontWeight: 700, fontSize: '0.95rem', color: '#fff',
                background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)', opacity: payingTax ? 0.7 : 1,
              }}
            >
              {payingTax ? t.taxPaying : t.taxPayButton}
            </button>
            <p style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: 10, lineHeight: 1.4 }}>{t.taxStripeNotice}</p>
          </>
        )}
      </div>
    );
  }

  if (notFound || !guide) {
    return (
      <div style={{ maxWidth: 480, margin: '40px auto 0', textAlign: 'center', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <LanguageToggle language={language} setLanguage={setLanguage} t={t} />
        </div>
        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🔒</div>
        <h2 style={{ color: '#1e293b', marginBottom: 8 }}>{t.notFoundTitle}</h2>
        <p style={{ color: '#64748b' }}>{t.notFoundBody}</p>
      </div>
    );
  }

  return (
    <GuideView
      guide={guide}
      language={language}
      setLanguage={setLanguage}
      managerOverlay={managerOverlay}
      onOverlayChange={setManagerOverlay}
      formToken={formToken}
      taxBanner={cityTaxDue && !(taxSummary && taxSummary.missingAmount <= 0) && (
        <div style={{ padding: '12px 14px', borderRadius: 12, background: '#fffbeb', border: '1px solid #fcd34d', marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
            <span style={{ fontSize: '1.3rem', flexShrink: 0, lineHeight: 1 }}>🧾</span>
            <div>
              <div style={{ fontWeight: 700, color: '#92400e', fontSize: '0.92rem', marginBottom: 2 }}>{t.taxDueBannerTitle}</div>
              <div style={{ fontSize: '0.82rem', color: '#78350f', lineHeight: 1.5 }}>{t.taxDueBannerBody}</div>
            </div>
          </div>
          {confirmingPayment ? (
            <p style={{ color: '#2563eb', fontWeight: 600, margin: 0 }}>{t.taxConfirming}</p>
          ) : taxSummaryError ? (
            <p style={{ color: '#dc2626', margin: 0 }}>{t.taxSummaryError}</p>
          ) : taxSummary && (
            <>
              {taxSummary.amountPaid > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#92400e', marginBottom: 6 }}>
                  <span>{t.taxAlreadyPaidLabel}</span>
                  <span style={{ fontWeight: 700 }}>{fmt(taxSummary.amountPaid)} {taxSummary.currency}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
                  {taxSummary.amountPaid > 0 ? t.taxMissingLabel : t.taxTotal}
                </span>
                <span style={{ fontWeight: 800, color: '#2563eb', fontSize: '1.15rem' }}>{fmt(taxSummary.missingAmount)} {taxSummary.currency}</span>
              </div>
              <button
                onClick={handlePayTax}
                disabled={payingTax}
                style={{
                  display: 'block', width: '100%', border: 'none', cursor: payingTax ? 'not-allowed' : 'pointer',
                  padding: '13px', borderRadius: 10, fontWeight: 700, fontSize: '0.95rem', color: '#fff',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)', opacity: payingTax ? 0.7 : 1,
                }}
              >
                {payingTax ? t.taxPaying : taxSummary.amountPaid > 0 ? t.taxPayMissingButton : t.taxPayButton}
              </button>
              <p style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: 10, marginBottom: 0, lineHeight: 1.4 }}>{t.taxStripeNotice}</p>
            </>
          )}
        </div>
      )}
    />
  );
};

export default CheckinGuidePage;
